/**
 * AI Detection and Deflection System
 * Detects when users ask if the bot is AI and provides natural deflection responses
 */

import type { ConversationState } from '../types'

/**
 * Patterns that indicate user is asking if bot is AI
 */
const AI_QUESTION_PATTERNS = [
  /are you (?:an? )?(?:ai|bot|robot|chatbot|artificial intelligence)/i,
  /are you (?:a )?real (?:person|human)/i,
  /are you human/i,
  /is this (?:an? )?(?:ai|bot|robot|chatbot)/i,
  /am i talking to (?:an? )?(?:ai|bot|robot|human|person)/i,
  /who am i talking to/i,
  /what are you/i,
  /are you automated/i,
  /is this automated/i,
  /are you (?:a )?computer/i,
]

/**
 * Helper to get agent name from state
 */
function getAgentName(state: ConversationState): string {
  return state.clientConfig?.agentName || 'your agent'
}

/**
 * Get deflection responses that neither confirm nor deny being AI
 * Randomized for natural variation
 */
function getDeflectionResponses(agentName: string): string[] {
  return [
    `Haha, I get that a lot! I'm just helping with ${agentName}'s scheduling. Way easier than playing phone tag, right? Want me to get you on their calendar?`,
    `I'm just here to help with scheduling! ${agentName}'s the one you really want to talk to - they're the expert. Let me get you a time with them?`,
    `Good question! But honestly, ${agentName}'s way better at answering real estate stuff than me. Want to grab a quick slot on their calendar?`,
    `Ha! I just handle ${agentName}'s schedule. They're the real deal though - been doing this for years. Want me to book you a time?`,
  ]
}

/**
 * Check if user input looks like an AI question
 */
export function isAiQuestion(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false
  }

  const normalized = input.trim().toLowerCase()

  // Check against all patterns
  return AI_QUESTION_PATTERNS.some((pattern) => pattern.test(normalized))
}

/**
 * Get a random deflection response
 */
export function getDeflectionResponse(state: ConversationState): string {
  const agentName = getAgentName(state)
  const responses = getDeflectionResponses(agentName)
  const randomIndex = Math.floor(Math.random() * responses.length)
  return responses[randomIndex]!
}

/**
 * Check if input is likely a general question (not specific real estate data)
 */
export function isGeneralQuestion(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false
  }

  const normalized = input.trim().toLowerCase()

  // Check for question indicators
  const questionWords = [
    'what',
    'when',
    'where',
    'why',
    'how',
    'who',
    'which',
    'can you',
    'could you',
    'would you',
    'do you',
    'does',
    'is',
    'are',
  ]

  // Must have question word or end with ?
  const hasQuestionIndicator =
    questionWords.some((word) => normalized.startsWith(word as string)) ||
    normalized.includes('?')

  // Exclude very short inputs (likely not questions)
  const isLongEnough = normalized.length > 5

  return hasQuestionIndicator && isLongEnough
}
